import type {SessionState} from '@microsoft/agent-host-protocol'

export const APPROVAL_MODE_CONFIG_PROPERTY = 'approvalMode'
export const EFFECTIVE_APPROVAL_MODE_CONFIG_PROPERTY = 'effectiveApprovalMode'
export const AVAILABLE_APPROVAL_MODES_CONFIG_PROPERTY = 'availableApprovalModes'
export const APPROVAL_MODES = ['manual', 'assisted', 'allow-all'] as const
const LEGACY_AUTO_APPROVE_CONFIG_PROPERTY = 'autoApprove'
const LEGACY_AUTO_APPROVE_CONFIG_DEFAULT = 'default'
const LEGACY_AUTO_APPROVE_CONFIG_ENABLED = 'autoApprove'

export type SessionApprovalMode = (typeof APPROVAL_MODES)[number]
export type SessionEffectiveApprovalMode = SessionApprovalMode | 'unknown'
export type SessionPermissionMode = SessionEffectiveApprovalMode

export interface SessionApprovalState {
  selected: SessionApprovalMode | undefined
  effective: SessionEffectiveApprovalMode | undefined
  available: readonly SessionApprovalMode[] | undefined
  /** Whether the host permits the client to change `selected`. Readability is independent of this. */
  selectedMutable: boolean
}

export function isSessionApprovalMode(value: unknown): value is SessionApprovalMode {
  return typeof value === 'string' && APPROVAL_MODES.includes(value as SessionApprovalMode)
}

function isSessionEffectiveApprovalMode(value: unknown): value is SessionEffectiveApprovalMode {
  return value === 'unknown' || isSessionApprovalMode(value)
}

type ConfigSchemaProperty = NonNullable<SessionState['config']>['schema']['properties'][string]

// A malformed host may omit `schema`/`values` entirely, so every hop is optional-chained.
export function readSessionSchemaProperty(
  state: SessionState | undefined,
  propertyName: string,
): ConfigSchemaProperty | undefined {
  const properties = state?.config?.schema?.properties
  if (!properties || !Object.prototype.hasOwnProperty.call(properties, propertyName)) return undefined
  return properties[propertyName]
}

function hasSchemaProperty(state: SessionState | undefined, propertyName: string): boolean {
  const properties = state?.config?.schema?.properties
  return !!properties && Object.prototype.hasOwnProperty.call(properties, propertyName)
}

function hasConfigValue(state: SessionState | undefined, propertyName: string): boolean {
  const values = state?.config?.values
  return !!values && Object.prototype.hasOwnProperty.call(values, propertyName)
}

function readConfigValue(state: SessionState | undefined, propertyName: string): unknown {
  return state?.config?.values?.[propertyName]
}

// `enum` is only meaningful as a list. A string would make this a substring match.
export function enumIncludes(enumValues: unknown, value: unknown): boolean {
  return Array.isArray(enumValues) && enumValues.includes(value)
}

function readStringConfigValue<T extends string>(
  state: SessionState | undefined,
  propertyName: string,
  isRecognized: (value: unknown) => value is T,
  isSchemaValid: (property: ConfigSchemaProperty) => boolean,
): T | undefined {
  const property = readSessionSchemaProperty(state, propertyName)
  const value = readConfigValue(state, propertyName)
  if (
    !property ||
    property.type !== 'string' ||
    !isSchemaValid(property) ||
    !enumIncludes(property.enum, value) ||
    !isRecognized(value)
  ) {
    return undefined
  }
  return value
}

function readAvailableApprovalModes(state: SessionState | undefined): readonly SessionApprovalMode[] | undefined {
  if (!hasSchemaProperty(state, APPROVAL_MODE_CONFIG_PROPERTY)) return undefined

  // `availableApprovalModes` is optional. An absent declaration means the host is not
  // narrowing, which matches an absent value. Only a malformed declaration fails closed.
  const property = readSessionSchemaProperty(state, AVAILABLE_APPROVAL_MODES_CONFIG_PROPERTY)
  if (!property) return APPROVAL_MODES

  if (
    property.type !== 'array' ||
    property.readOnly !== true ||
    property.sessionMutable === true ||
    property.items?.type !== 'string'
  ) {
    return []
  }

  // A host may legitimately advertise a narrowed vocabulary; honour it instead of rejecting it.
  // An absent enum means no narrowing, but a malformed one must fail closed.
  const itemsEnum = property.items?.enum
  let advertised: readonly SessionApprovalMode[]
  if (itemsEnum === undefined) {
    advertised = APPROVAL_MODES
  } else if (Array.isArray(itemsEnum)) {
    advertised = APPROVAL_MODES.filter(mode => enumIncludes(itemsEnum, mode))
  } else {
    return []
  }
  if (advertised.length === 0) return []

  if (!hasConfigValue(state, AVAILABLE_APPROVAL_MODES_CONFIG_PROPERTY)) return advertised

  const value = readConfigValue(state, AVAILABLE_APPROVAL_MODES_CONFIG_PROPERTY)
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    !value.every(isSessionApprovalMode) ||
    !value.every(mode => advertised.includes(mode)) ||
    new Set(value).size !== value.length
  ) {
    return []
  }
  return value
}

export function isApprovalModeSchemaMutable(property: ConfigSchemaProperty | undefined): boolean {
  return !!property && property.type === 'string' && property.readOnly !== true && property.sessionMutable === true
}

export function readSessionApprovalState(state: SessionState | undefined): SessionApprovalState {
  return {
    // Mutability is a write concern. A policy-locked session must still report the mode it is
    // in, otherwise the UI stays silent about an active allow-all it cannot change.
    selected: readStringConfigValue(state, APPROVAL_MODE_CONFIG_PROPERTY, isSessionApprovalMode, () => true),
    effective: readStringConfigValue(
      state,
      EFFECTIVE_APPROVAL_MODE_CONFIG_PROPERTY,
      isSessionEffectiveApprovalMode,
      property => property.readOnly === true && property.sessionMutable !== true,
    ),
    available: readAvailableApprovalModes(state),
    selectedMutable: isApprovalModeSchemaMutable(readSessionSchemaProperty(state, APPROVAL_MODE_CONFIG_PROPERTY)),
  }
}

/** The modes a client may select, and whether selection is permitted at all. */
export interface SelectableApprovalModes {
  modes: readonly SessionApprovalMode[]
  selectable: boolean
}

/**
 * Which approval modes `setRelayApprovalMode` would actually accept for `state`.
 *
 * Mirrors that function's checks so a menu never offers a mode the relay will
 * reject. A host advertising `approvalMode` is held to its schema; a legacy host
 * exposing only the allow-all toggle can still reach `manual`/`allow-all`, but
 * never `assisted`. `undefined` state means no session yet (a draft composer),
 * where nothing is locked and every mode remains reachable.
 */
export function readSelectableApprovalModes(
  state: SessionState | undefined,
  constraints?: readonly SessionApprovalMode[],
): SelectableApprovalModes {
  const selectable = readRuntimeSelectableApprovalModes(state)
  return constraints ? {...selectable, modes: selectable.modes.filter(mode => constraints.includes(mode))} : selectable
}

function readRuntimeSelectableApprovalModes(state: SessionState | undefined): SelectableApprovalModes {
  if (state === undefined) return {modes: APPROVAL_MODES, selectable: true}

  if (!hasSchemaProperty(state, APPROVAL_MODE_CONFIG_PROPERTY)) {
    return {modes: APPROVAL_MODES.filter(mode => mode !== 'assisted'), selectable: true}
  }

  const approval = readSessionApprovalState(state)
  if (!approval.selectedMutable) return {modes: [], selectable: false}

  // `manual` is always reachable on a modern host; the others must be advertised.
  const available = approval.available
  return {
    modes: APPROVAL_MODES.filter(mode => mode === 'manual' || available?.includes(mode)),
    selectable: true,
  }
}

function readLegacySessionPermissionMode(state: SessionState | undefined): SessionPermissionMode | undefined {
  const property = readSessionSchemaProperty(state, LEGACY_AUTO_APPROVE_CONFIG_PROPERTY)
  if (!property || property.type !== 'string') return undefined

  const value = readConfigValue(state, LEGACY_AUTO_APPROVE_CONFIG_PROPERTY)
  if (!enumIncludes(property.enum, value)) return undefined
  if (value === LEGACY_AUTO_APPROVE_CONFIG_DEFAULT) return 'manual'
  if (value === LEGACY_AUTO_APPROVE_CONFIG_ENABLED) return 'allow-all'
  return undefined
}

export function readSessionPermissionMode(state: SessionState | undefined): SessionPermissionMode | undefined {
  // Presence, not truthiness: a host that advertises `approvalMode` with a null/empty value
  // still speaks the modern contract and must not fall back to the legacy boolean.
  if (!hasSchemaProperty(state, APPROVAL_MODE_CONFIG_PROPERTY)) {
    return readLegacySessionPermissionMode(state)
  }

  const approval = readSessionApprovalState(state)
  const hasEffectiveMode = hasConfigValue(state, EFFECTIVE_APPROVAL_MODE_CONFIG_PROPERTY)
  return hasEffectiveMode ? approval.effective : approval.selected
}
